import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useAnimationConfig, useReducedMotion } from '../lib/animations/context';

interface EnhancedSubmitButtonProps {
  isLoading?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  children?: React.ReactNode;
  className?: string;
  loadingText?: string;
  successText?: string;
  errorText?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'small' | 'medium' | 'large';
  icon?: React.ReactNode;
  fullWidth?: boolean;
  type?: 'button' | 'submit';
}

const EnhancedSubmitButton: React.FC<EnhancedSubmitButtonProps> = ({
  isLoading = false,
  isSuccess = false,
  isError = false,
  disabled = false,
  onClick,
  children = 'Submit',
  className = '',
  loadingText = 'Submitting...',
  successText = 'Success!',
  errorText = 'Try Again',
  variant = 'primary',
  size = 'medium',
  icon,
  fullWidth = false,
  type = 'submit'
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const [ripples, setRipples] = useState<Array<{ id: number; x: number; y: number }>>([]);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  // Button state logic
  const getButtonState = () => {
    if (isSuccess) return 'success';
    if (isError) return 'error';
    if (isLoading) return 'loading';
    if (disabled) return 'disabled';
    return 'default';
  };

  const buttonState = getButtonState();

  // Variant styles
  const variantStyles = {
    primary: {
      default: 'bg-[#FF5A1F] text-white border-[#FF5A1F]',
      hover: 'bg-[#e4501c] border-[#e4501c]',
      disabled: 'bg-gray-300 text-gray-500 border-gray-300',
      loading: 'bg-[#FF5A1F] text-white border-[#FF5A1F]',
      success: 'bg-green-500 text-white border-green-500',
      error: 'bg-red-500 text-white border-red-500'
    },
    secondary: {
      default: 'bg-gray-100 text-gray-700 border-gray-300',
      hover: 'bg-gray-200 border-gray-400',
      disabled: 'bg-gray-50 text-gray-400 border-gray-200',
      loading: 'bg-gray-100 text-gray-700 border-gray-300',
      success: 'bg-green-100 text-green-700 border-green-300',
      error: 'bg-red-100 text-red-700 border-red-300'
    },
    outline: {
      default: 'bg-transparent text-[#FF5A1F] border-[#FF5A1F]',
      hover: 'bg-[#FF5A1F] text-white border-[#FF5A1F]',
      disabled: 'bg-transparent text-gray-400 border-gray-300',
      loading: 'bg-transparent text-[#FF5A1F] border-[#FF5A1F]',
      success: 'bg-transparent text-green-600 border-green-500',
      error: 'bg-transparent text-red-600 border-red-500'
    }
  };

  // Size styles
  const sizeStyles = {
    small: 'px-4 py-2 text-sm',
    medium: 'px-6 py-3 text-base',
    large: 'px-8 py-4 text-lg'
  };

  // Animation variants
  const buttonVariants = {
    default: {
      scale: 1,
      y: 0,
      transition: { duration: config.customTimings.fast }
    },
    hover: {
      scale: isReducedMotion ? 1 : 1.02,
      y: isReducedMotion ? 0 : -2,
      transition: { duration: config.customTimings.fast }
    },
    pressed: {
      scale: isReducedMotion ? 1 : 0.98,
      y: 0,
      transition: { duration: config.customTimings.fast }
    },
    loading: {
      scale: 1,
      y: 0,
      transition: { duration: config.customTimings.fast }
    },
    success: {
      scale: isReducedMotion ? 1 : 1.05,
      y: 0,
      transition: { 
        duration: config.customTimings.fast,
        type: "spring",
        stiffness: 300
      }
    },
    error: {
      scale: 1,
      x: isReducedMotion ? 0 : [-5, 5, -5, 5, 0],
      transition: { 
        duration: config.customTimings.fast,
        times: [0, 0.2, 0.4, 0.6, 1]
      }
    }
  };

  const contentVariants = {
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: config.customTimings.fast }
    },
    hidden: {
      opacity: 0,
      y: 10,
      transition: { duration: config.customTimings.fast }
    }
  };

  const iconVariants = {
    default: {
      rotate: 0,
      scale: 1,
      transition: { duration: config.customTimings.fast }
    },
    loading: {
      rotate: isReducedMotion ? 0 : 360,
      scale: 1,
      transition: isReducedMotion ? {} : {
        rotate: {
          duration: 1,
          repeat: Infinity,
          ease: "linear"
        }
      }
    },
    success: {
      rotate: 0,
      scale: isReducedMotion ? 1 : [1, 1.2, 1],
      transition: {
        scale: {
          duration: 0.3,
          times: [0, 0.5, 1]
        }
      }
    }
  };

  // Ripple effect
  const createRipple = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (isReducedMotion || disabled || isLoading) return;

    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const newRipple = {
      id: Date.now(),
      x,
      y
    };

    setRipples(prev => [...prev, newRipple]);

    // Remove ripple after animation
    setTimeout(() => {
      setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
    }, 600);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled || isLoading) return;
    
    createRipple(event);
    onClick?.();
  };

  const handleMouseDown = () => setIsPressed(true);
  const handleMouseUp = () => setIsPressed(false);
  const handleMouseLeave = () => setIsPressed(false);

  // Get current content and icon
  const getCurrentContent = () => {
    switch (buttonState) {
      case 'loading':
        return loadingText;
      case 'success':
        return successText;
      case 'error':
        return errorText;
      default:
        return children;
    }
  };

  const getCurrentIcon = () => {
    switch (buttonState) {
      case 'loading':
        return <Loader2 className="w-5 h-5" />;
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return icon || <Send className="w-5 h-5" />;
    }
  };

  const currentVariantStyle = variantStyles[variant][buttonState] || variantStyles[variant].default;
  const currentSizeStyle = sizeStyles[size];

  return (
    <motion.button
      type={type}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
      disabled={disabled || isLoading}
      className={`
        relative overflow-hidden font-semibold rounded-lg border-2 transition-all duration-200
        ${currentVariantStyle}
        ${currentSizeStyle}
        ${fullWidth ? 'w-full' : ''}
        ${disabled || isLoading ? 'cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      variants={buttonVariants}
      animate={isPressed ? 'pressed' : buttonState}
      whileHover={!disabled && !isLoading ? 'hover' : undefined}
      initial="default"
    >
      {/* Ripple Effects */}
      <AnimatePresence>
        {ripples.map((ripple) => (
          <motion.div
            key={ripple.id}
            className="absolute rounded-full bg-white opacity-30 pointer-events-none"
            style={{
              left: ripple.x,
              top: ripple.y,
              transform: 'translate(-50%, -50%)'
            }}
            initial={{ width: 0, height: 0 }}
            animate={{ 
              width: 200, 
              height: 200,
              opacity: [0.3, 0]
            }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            exit={{ opacity: 0 }}
          />
        ))}
      </AnimatePresence>

      {/* Button Content */}
      <div className="flex items-center justify-center space-x-2 relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={buttonState}
            className="flex items-center space-x-2"
            variants={contentVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            <motion.div
              variants={iconVariants}
              animate={buttonState === 'loading' ? 'loading' : buttonState === 'success' ? 'success' : 'default'}
            >
              {getCurrentIcon()}
            </motion.div>
            <span>{getCurrentContent()}</span>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Loading Progress Bar */}
      {isLoading && (
        <motion.div
          className="absolute bottom-0 left-0 h-1 bg-white bg-opacity-30 rounded-full"
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ 
            duration: 2,
            ease: "easeInOut",
            repeat: Infinity
          }}
        />
      )}
    </motion.button>
  );
};

export default EnhancedSubmitButton;